import { fundsRepository } from '@trader-dashboard/database';
const TWO_WEEKS_IN_MS = 14 * 24 * 60 * 60 * 1000;
/**
 * Supabase-based Funds API Service
 * Replaces Firebase Functions with direct Supabase database calls
 */
export class FundsApiServiceSupabase {
    constructor(storage) {
        this.storage = storage;
    }
    loadCache(accountId) {
        const cachedData = this.storage.getItem(`cachedFundsTransactions_${accountId}`);
        const cachedTime = this.storage.getItem(`lastFetchTime_${accountId}`);
        return {
            cachedFundsTransactions: cachedData ? JSON.parse(cachedData) : null,
            lastFetchTime: cachedTime ? parseInt(cachedTime, 10) : 0,
        };
    }
    saveCache(accountId, data) {
        this.storage.setItem(`cachedFundsTransactions_${accountId}`, JSON.stringify(data));
        this.storage.setItem(`lastFetchTime_${accountId}`, Date.now().toString());
    }
    /**
     * Convert Supabase FundsTransaction to UI Transaction format
     */
    toUITransaction(dbTransaction) {
        return {
            id: dbTransaction.id,
            account_id: dbTransaction.account_id,
            ticket: dbTransaction.ticket ?? 0,
            transaction_time: dbTransaction.transaction_time,
            transaction_type: dbTransaction.transaction_type,
            amount: dbTransaction.amount,
            comment: dbTransaction.comment,
            created_at: dbTransaction.created_at,
            updated_at: dbTransaction.updated_at,
            // Legacy fields for backward compatibility
            type: (dbTransaction.transaction_type === 'deposit' || dbTransaction.transaction_type === 'withdrawal')
                ? dbTransaction.transaction_type
                : undefined,
            date: new Date(dbTransaction.transaction_time * 1000).toISOString(),
        };
    }
    async getFundsTransactions(accountId, ignoreCache = false) {
        try {
            const { cachedFundsTransactions, lastFetchTime } = this.loadCache(accountId);
            const currentTime = Date.now();
            if (!ignoreCache && cachedFundsTransactions && (currentTime - lastFetchTime) < TWO_WEEKS_IN_MS) {
                console.log('FundsApiSupabase: Using cached data');
                return cachedFundsTransactions;
            }
            console.log('FundsApiSupabase: Fetching from Supabase for account:', accountId);
            // Fetch all transactions for the account, ordered by transaction_time descending
            const result = await fundsRepository.getTransactions(accountId, {
                limit: 1000, // Get all transactions
                offset: 0,
            });
            const transactions = result.data.map(t => this.toUITransaction(t));
            const response = {
                transactions,
            };
            console.log('FundsApiSupabase: Fetched', transactions.length, 'transactions');
            // Update the cache
            this.saveCache(accountId, response);
            return response;
        }
        catch (e) {
            console.error('FundsApiSupabase.getFundsTransactions error:', e);
            return undefined;
        }
    }
    async updateTransaction(accountId, id, type, amount, date) {
        try {
            console.log('FundsApiSupabase: Updating transaction', { accountId, id, type, amount, date });
            const transaction_time = Math.floor(date.getTime() / 1000);
            const transactionAmount = type === 'withdrawal' ? -Math.abs(amount) : Math.abs(amount);
            const updated = await fundsRepository.updateTransaction(id, {
                transaction_type: type,
                amount: transactionAmount,
                transaction_time,
            });
            console.log('FundsApiSupabase: Transaction updated', updated);
            // Clear cache to force refresh
            this.storage.setItem(`lastFetchTime_${accountId}`, '0');
            return updated;
        }
        catch (e) {
            console.error('FundsApiSupabase.updateTransaction error:', e);
            throw e;
        }
    }
    async addTransaction(accountId, type, amount, date) {
        try {
            console.log('FundsApiSupabase: Adding transaction', { accountId, type, amount, date });
            // Generate a unique ticket number (timestamp-based)
            const ticket = Date.now();
            const transaction_time = Math.floor(date.getTime() / 1000);
            // For withdrawals, make amount negative
            const transactionAmount = type === 'withdrawal' ? -Math.abs(amount) : Math.abs(amount);
            const newTransaction = await fundsRepository.addTransaction({
                account_id: accountId,
                ticket,
                transaction_time,
                transaction_type: type,
                amount: transactionAmount,
                comment: `${type.charAt(0).toUpperCase() + type.slice(1)} via dashboard`,
            });
            console.log('FundsApiSupabase: Transaction added', newTransaction);
            // Clear cache to force refresh
            this.storage.setItem(`lastFetchTime_${accountId}`, '0');
            return newTransaction;
        }
        catch (e) {
            console.error('FundsApiSupabase.addTransaction error:', e);
            throw e;
        }
    }
}
